import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./create-public.page.scss";
import React, { useEffect, useMemo, useState } from "react";
import {
    getPreFillResume, getSummaryResume, publicPostAddCandidate,
    publicRoomDetail, saveLog, SendEmailDropCV, uploadResume,
} from "../services/interview-api.services";
import { JobPosition, CandidateFieldConfig, CandidateRequireConfigModel } from "../models/questions.model";
import { CandidatePublic } from "../models/candidate-public.model";
import { CircularProgress } from "@mui/material";
import config from "../config";
import { UtmObject } from "../models/utm-object.model";
import { useLanguage } from "../providers/language.provider";
import { setPublicInterviewBranding } from "../utils/public-branding.util";
import IMG_JOBHACK from "../assets/images/logo-jobhack.png";

const CreatePublicPage = () => {
    const { company_id, job_question_id } = useParams<{ company_id: string; job_question_id: string }>();
    const { switchLanguage } = useLanguage();
    const [jobPosition, setJobPosition] = useState<JobPosition>();
    const [resumeKey, setResumeKey] = useState("");
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
        resume: null as File | null,
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [logoLoadFailed, setLogoLoadFailed] = useState(false);
    const [fileHover, setFileHover] = useState(false);
    const location = useLocation();
    const [fullUrl, setFullUrl] = useState<string>("");
    const [utmObject, setUtmObject] = useState<UtmObject | null>(null);

    useEffect(() => {
        const { pathname, search } = location;
        setFullUrl(`${pathname}${search}`);
        const p = new URLSearchParams(search);
        const utmSource = p.get("utm_source");
        const utmMedium = p.get("utm_medium");
        if (utmSource?.trim() && utmMedium?.trim()) {
            setUtmObject({ utm_source: utmSource, utm_medium: utmMedium });
        }
    }, [location]);

    useEffect(() => {
        if (job_question_id === "9b13f125-8003-40b3-8b4d-a81c390fef7f") {
            switchLanguage("en");
        }
    }, [job_question_id, switchLanguage]);

    useEffect(() => {
        publicRoomDetail(job_question_id, company_id).then((res) => {
            if (res.data) {
                setJobPosition(res.data);
                // Persist branding so interview-room + result pages keep the same
                // company logo even when their own API responses omit it.
                const jp = res.data as JobPosition & { logoImageUrl?: string };
                const resolvedLogo = String(jp.company_logo || jp.logoImageUrl || "").trim();
                setPublicInterviewBranding(company_id, job_question_id, {
                    logoUrl: resolvedLogo,
                    companyName: String(jp.company_name || "").trim(),
                });
                // #region agent log
                fetch('http://127.0.0.1:7306/ingest/c0ca0ffc-101a-41d9-9a0f-54f5fb097d04',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'04060b'},body:JSON.stringify({sessionId:'04060b',hypothesisId:'A,B,C',location:'create-public.page.tsx:publicRoomDetail.then',message:'publicRoomDetail resolved + stash written',data:{company_id_param:company_id,job_question_id_param:job_question_id,api_company_id:(jp as any)?.company_id,api_company_logo:jp.company_logo,api_logoImageUrl:(jp as any).logoImageUrl,api_company_name:jp.company_name,resolvedLogo,storageKey:`public_interview_branding:${company_id}:${job_question_id}`,storageWrittenValue:(()=>{try{return sessionStorage.getItem(`public_interview_branding:${company_id}:${job_question_id}`);}catch(e){return 'ERR:'+(e as Error).message;}})(),href:typeof window!=='undefined'?window.location.href:'',isIframe:typeof window!=='undefined'?(window.self!==window.top):'unknown'},timestamp:Date.now()})}).catch(()=>{});
                // #endregion
            }
        });
    }, [company_id, job_question_id]);

    useEffect(() => {
        setLogoLoadFailed(false);
    }, [jobPosition]);

    const candidateFieldConfig = useMemo(() => {
        const requireConfig = (jobPosition as any)?.candidate_require_config as CandidateRequireConfigModel | undefined;
        const requiredFallback = (jobPosition as any)?.candidate_require || {};
        const getField = (key: "firstname" | "lastname" | "email" | "phone"): CandidateFieldConfig => {
            const cfg = requireConfig?.[key];
            if (cfg && typeof cfg.show === "boolean" && (cfg.mode === "required" || cfg.mode === "optional")) {
                return cfg;
            }
            return {
                show: Boolean(requiredFallback?.[key]),
                mode: requiredFallback?.[key] ? "required" : "optional",
            };
        };
        return {
            firstname: getField("firstname"),
            lastname: getField("lastname"),
            email: getField("email"),
            phone: getField("phone"),
        };
    }, [jobPosition]);

    const validate = (): boolean => {
        if (!jobPosition) return false;
        const newErrors: Record<string, string> = {};

        if (candidateFieldConfig.firstname.show && candidateFieldConfig.firstname.mode === "required" && !formData.firstname)
            newErrors.firstname = "First name is required";

        if (candidateFieldConfig.lastname.show && candidateFieldConfig.lastname.mode === "required" && !formData.lastname)
            newErrors.lastname = "Last name is required";

        if (candidateFieldConfig.email.show) {
            if (candidateFieldConfig.email.mode === "required" && !formData.email)
                newErrors.email = "Email is required";
            else if (formData.email && !/\S+@\S+\.\S+/.test(formData.email))
                newErrors.email = "Email address is invalid";
        }

        if (candidateFieldConfig.phone.show) {
            const phoneValue = formData.phone.trim();
            if (candidateFieldConfig.phone.mode === "required" && !phoneValue)
                newErrors.phone = "Phone number is required";
            else if (phoneValue && !/^[0-9+\-\s()]{8,20}$/.test(phoneValue))
                newErrors.phone = "Phone number is invalid";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, files } = e.target;
        if (name === "resume" && files) {
            setFormData({ ...formData, resume: files[0] });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!validate()) return;

        const emailToUse = formData.email.trim() || `public.${Date.now()}@interview.ai`;
        const input: CandidatePublic = {
            firstname: formData.firstname,
            lastname: formData.lastname,
            email: emailToUse,
            phone: formData.phone.trim(),
            status: "ACTIVE",
            jobTitle: "N/A",
            interview_start_date: new Date().toISOString(),
            interview_end_date: new Date().toISOString(),
            jobExp: 0,
            resume: null,
            candidate_id: "",
        };

        if (formData.resume) {
            setLoading(true);
            uploadResume(formData.resume, formData.resume.name, "public_interview_ai")
                .then((res) => {
                    if (res) {
                        setResumeKey(res.resume_key);
                        saveLog({
                            company_id, job_question_id,
                            event: "UPLOAD_RESUME",
                            type: "public_interview_ai",
                            url: fullUrl,
                            utm_source: utmObject?.utm_source || "",
                            utm_medium: utmObject?.utm_medium || "",
                            log: "Upload Resume Complete",
                            resume_key: res.resume_key,
                        });
                        SendEmailDropCV(jobPosition?.company_name);
                        try {
                            getSummaryResume(res.resume_key).then((resSum) => {
                                if (resSum.summary) {
                                    input.resume = { key: res.resume_key, filename: formData.resume!.name, summary: resSum.summary, skills: resSum.skills };
                                    publicPostAddCandidate(job_question_id, company_id, input).then((res) => {
                                        if (res.is_success) {
                                            setLoading(false);
                                            navigate(`/public/verify/${company_id}/${job_question_id}/${res["result"].candidate_id}?app_id=PUBLIC_INTERVIEW_AI`);
                                        }
                                    });
                                }
                            });
                        } catch {
                            input.resume = { key: res.resume_key, filename: formData.resume!.name, summary: "", skills: [] };
                            publicPostAddCandidate(job_question_id, company_id, input).then((res) => {
                                if (res.is_success) {
                                    setLoading(false);
                                    navigate(`/public/verify/${company_id}/${job_question_id}/${res["result"].candidate_id}?app_id=PUBLIC_INTERVIEW_AI`);
                                }
                            });
                        }
                    }
                })
                .catch((err) => {
                    console.error("Error uploading resume:", err);
                    alert("เกิดข้อผิดพลาดในการอัปโหลดเรซูเม่ (Error uploading resume)");
                });
        } else {
            publicPostAddCandidate(job_question_id, company_id, input).then((res) => {
                if (res.is_success) {
                    navigate(`/public/verify/${company_id}/${job_question_id}/${res["result"].candidate_id}?app_id=PUBLIC_INTERVIEW_AI`);
                }
            });
        }
    };

    // ── Still loading job data ─────────────────────────────
    if (!jobPosition) {
        return (
            <div className="cp-page-loading">
                <CircularProgress size={22} sx={{ color: "#7c3aed" }} />
                <span>Loading…</span>
            </div>
        );
    }

    const { candidate_require, company_name, company_logo, job_title, name: jobPositionName } = jobPosition;
    const questionSetDisplayName = String(jobPositionName || job_title || "").trim() || "Interview";
    // Prefer company logo (resolved server-side from interview_company by company_id);
    // fall back to the JobHack logo when the company has no logo or the image fails to load.
    const logoUrl = String(company_logo || (jobPosition as any).logoImageUrl || "").trim();
    const useCompanyLogo = Boolean(logoUrl) && !logoLoadFailed;
    const displayedLogoSrc = useCompanyLogo ? logoUrl : IMG_JOBHACK;
    const displayedLogoAlt = useCompanyLogo ? `${company_name || "Company"} logo` : "JobHack";

    // ── Label helper ──────────────────────────────────────
    const FieldLabel = ({ text, mode }: { text: string; mode: "required" | "optional" }) => (
        <label className="cp-label">
            {text}
            {mode === "required"
                ? <span className="cp-required">*</span>
                : <span className="cp-optional">optional</span>}
        </label>
    );

    return (
        <div className="cp-page">
            <div className="cp-card">
                {/* ── Header ── */}
                <div className="cp-header">
                    <img
                        src={displayedLogoSrc}
                        alt={displayedLogoAlt}
                        className="cp-logo"
                        onError={() => setLogoLoadFailed(true)}
                    />
                    <h1 className="cp-job-title">{questionSetDisplayName}</h1>
                </div>

                {/* ── Form body ── */}
                <div className="cp-form-body">
                    {loading ? (
                        <div className="cp-loading">
                            <CircularProgress size={32} sx={{ color: "#7c3aed" }} />
                            <span className="cp-loading-text">กำลังดึงข้อมูลจาก Resume…</span>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="cp-form" noValidate>
                            {/* First name */}
                            {candidateFieldConfig.firstname.show && (
                                <div className="cp-field">
                                    <FieldLabel text="First Name" mode={candidateFieldConfig.firstname.mode} />
                                    <input
                                        type="text"
                                        id="firstname"
                                        name="firstname"
                                        value={formData.firstname}
                                        onChange={handleChange}
                                        placeholder="e.g. Somchai"
                                        className={`cp-input${errors.firstname ? " cp-input-error" : ""}`}
                                    />
                                    {errors.firstname && <span className="cp-error">{errors.firstname}</span>}
                                </div>
                            )}

                            {/* Last name */}
                            {candidateFieldConfig.lastname.show && (
                                <div className="cp-field">
                                    <FieldLabel text="Last Name" mode={candidateFieldConfig.lastname.mode} />
                                    <input
                                        type="text"
                                        id="lastname"
                                        name="lastname"
                                        value={formData.lastname}
                                        onChange={handleChange}
                                        placeholder="e.g. Raksa"
                                        className={`cp-input${errors.lastname ? " cp-input-error" : ""}`}
                                    />
                                    {errors.lastname && <span className="cp-error">{errors.lastname}</span>}
                                </div>
                            )}

                            {/* Email */}
                            {candidateFieldConfig.email.show && (
                                <div className="cp-field">
                                    <FieldLabel text="Email" mode={candidateFieldConfig.email.mode} />
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="e.g. name@email.com"
                                        className={`cp-input${errors.email ? " cp-input-error" : ""}`}
                                    />
                                    {errors.email && <span className="cp-error">{errors.email}</span>}
                                </div>
                            )}

                            {/* Phone */}
                            {candidateFieldConfig.phone.show && (
                                <div className="cp-field">
                                    <FieldLabel text="Phone Number" mode={candidateFieldConfig.phone.mode} />
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="e.g. 081-234-5678"
                                        className={`cp-input${errors.phone ? " cp-input-error" : ""}`}
                                    />
                                    {errors.phone && <span className="cp-error">{errors.phone}</span>}
                                </div>
                            )}

                            {/* Resume upload */}
                            {candidate_require.resume && (
                                <div className="cp-field">
                                    <label className="cp-label">
                                        Resume / CV
                                        <span className="cp-optional">PDF, DOC, DOCX, PNG, JPG · max 5 MB</span>
                                    </label>
                                    <div
                                        className={`cp-file-zone${formData.resume ? " cp-file-zone-filled" : ""}${fileHover ? " cp-file-zone-hover" : ""}`}
                                        onDragOver={(e) => { e.preventDefault(); setFileHover(true); }}
                                        onDragLeave={() => setFileHover(false)}
                                        onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                                            e.preventDefault();
                                            setFileHover(false);
                                            const file = e.dataTransfer.files[0];
                                            if (file) setFormData((prev) => ({ ...prev, resume: file }));
                                        }}
                                    >
                                        <input
                                            type="file"
                                            id="resume"
                                            name="resume"
                                            onChange={handleChange}
                                            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                                        />
                                        {formData.resume ? (
                                            <>
                                                <span className="cp-file-icon">📄</span>
                                                <span className="cp-file-name">{formData.resume.name}</span>
                                                <span className="cp-file-sub-text">Click to change file</span>
                                            </>
                                        ) : (
                                            <>
                                                <span className="cp-file-icon">⬆</span>
                                                <span className="cp-file-main-text">Click or drag to upload resume</span>
                                                <span className="cp-file-sub-text">PDF, DOC, DOCX, PNG, JPG (max 5 MB)</span>
                                            </>
                                        )}
                                    </div>
                                    {errors.resume && <span className="cp-error">{errors.resume}</span>}
                                </div>
                            )}

                            <button type="submit" className="cp-submit">
                                Submit Application
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreatePublicPage;
